import { control, curve, display, readout, type PhysicsDomain } from './domainTypes';

export function collision(m1: number, m2: number, u1: number, u2: number, e: number) {
  const p = m1 * u1 + m2 * u2;
  const v1 = (p - m2 * e * (u1 - u2)) / (m1 + m2);
  const v2 = (p + m1 * e * (u1 - u2)) / (m1 + m2);
  const before = (m1 * u1 ** 2 + m2 * u2 ** 2) / 2;
  const after = (m1 * v1 ** 2 + m2 * v2 ** 2) / 2;
  return {
    v1,
    v2,
    p,
    before,
    after,
    lost: Math.max(0, before - after),
    impulse: m1 * (v1 - u1),
  };
}

export const momentum: PhysicsDomain = {
  id: 'momentum',
  name: 'Momentum & Collisions Lab',
  hint: 'Compare signed momentum, collision outcomes and the force needed to stop an object.',
  experiments: [
    {
      id: 'collision',
      name: 'Two-cart collision',
      question: 'What stays constant when colliding carts stick together?',
      controls: [
        control('m1', 'Cart A mass', 'kg', 0.1, 10, 1, 0.1),
        control('m2', 'Cart B mass', 'kg', 0.1, 10, 1, 0.1),
        control('u1', 'Cart A initial velocity', 'm/s', -10, 10, 4, 0.5),
        control('u2', 'Cart B initial velocity', 'm/s', -10, 10, 0, 0.5),
        control('e', 'Restitution: 0 sticks, 1 elastic', '', 0, 1, 1, 0.1),
      ],
      examples: [
        {
          id: 'elastic',
          name: 'Equal carts bounce',
          values: {},
          note: 'Equal masses exchange velocities in an elastic collision.',
        },
        {
          id: 'stick',
          name: 'Carts stick together',
          values: { e: 0 },
          note: 'Momentum stays constant while kinetic energy decreases.',
        },
        {
          id: 'head-on',
          name: 'Head-on and stick',
          values: { u2: -4, e: 0 },
          note: 'Opposite momenta can cancel even though both carts were moving.',
        },
        {
          id: 'heavy',
          name: 'Heavy cart meets light cart',
          values: { m1: 5 },
          note: 'Both carts receive equal and opposite impulses.',
        },
      ],
      assumptions:
        'One-dimensional point carts with no external impulse. Positive velocity points right. The calculation describes a specified contact event. Playback starts point carts 2 m apart, with A left of B. Contact occurs only if A catches B. The camera follows their centre of mass; displayed velocities use the laboratory frame. Contact is instantaneous. Restitution is a material parameter, not friction.',
      source:
        'https://openstax.org/books/university-physics-volume-1/pages/9-4-types-of-collisions',
      calculate: ({ m1, m2, u1, u2, e }) => {
        const r = collision(m1, m2, u1, u2, e);
        return {
          readouts: [
            readout('Cart A final velocity', r.v1, 'm/s'),
            readout('Cart B final velocity', r.v2, 'm/s'),
            readout('Momentum before', r.p, 'kg m/s'),
            readout('Momentum after', m1 * r.v1 + m2 * r.v2, 'kg m/s'),
            readout('Initial kinetic energy', r.before, 'J'),
            readout('Final kinetic energy', r.after, 'J'),
            readout('Kinetic energy converted', r.lost, 'J'),
            readout('Impulse on A', r.impulse, 'N s'),
          ],
          summary:
            e === 0
              ? 'The carts leave contact with the same velocity. Total momentum is conserved.'
              : 'Both carts receive equal and opposite impulses; total momentum is conserved.',
          working: [
            `p = m₁u₁ + m₂u₂ = ${display(r.p)} kg m/s`,
            'v₂ − v₁ = e(u₁ − u₂)',
            `v₁ = [p − m₂e(u₁ − u₂)] / (m₁ + m₂) = ${display(r.v1)} m/s`,
            `v₂ = [p + m₁e(u₁ − u₂)] / (m₁ + m₂) = ${display(r.v2)} m/s`,
            `Converted KE = KEbefore − KEafter = ${display(r.lost)} J`,
          ],
          plot: curve(1, (restitution) => collision(m1, m2, u1, u2, restitution).lost),
          xLabel: 'Restitution',
          yLabel: 'Converted KE (J)',
        };
      },
      prediction: {
        question:
          'Two equal carts stick after one strikes the stationary other. What remains constant?',
        options: ['Total momentum', 'Total kinetic energy', 'Each cart velocity'],
        answer: 0,
        why: 'With no external impulse, system momentum is conserved. Some kinetic energy becomes internal energy.',
      },
      task: 'Predict the common final velocity of a 2 kg cart at 3 m/s striking a 1 kg stationary cart and sticking. Test it.',
    },
    {
      id: 'impulse',
      name: 'Impulse and stopping',
      question: 'Why can a longer stopping time reduce force without changing the momentum change?',
      controls: [
        control('mass', 'Object mass', 'kg', 0.1, 10, 1, 0.1),
        control('initial', 'Initial velocity', 'm/s', -20, 20, 10, 0.5),
        control('final', 'Final velocity', 'm/s', -20, 20, 0, 0.5),
        control('duration', 'Interaction duration', 's', 0.01, 2, 0.1, 0.01),
      ],
      examples: [
        {
          id: 'short',
          name: 'Quick stop',
          values: {},
          note: 'A short stop requires a large average force.',
        },
        {
          id: 'long',
          name: 'Cushioned stop',
          values: { duration: 0.5 },
          note: 'A longer interaction spreads the same impulse over more time.',
        },
        {
          id: 'bounce',
          name: 'Bounce backwards',
          values: { final: -10 },
          note: 'Reversal creates a larger momentum change than stopping.',
        },
      ],
      assumptions:
        'One-dimensional motion. Force shown is the net average force over the interval, not the peak force. Positive velocity and force point right.',
      source:
        'https://openstax.org/books/university-physics-volume-1/pages/9-2-impulse-and-collisions',
      calculate: ({ mass, initial, final, duration }) => {
        const impulse = mass * (final - initial);
        const force = impulse / duration;
        return {
          readouts: [
            readout('Momentum change', impulse, 'kg m/s'),
            readout('Impulse', impulse, 'N s'),
            readout('Average net force', force, 'N'),
          ],
          summary: `The momentum change is ${display(impulse)} kg m/s. A longer interaction reduces average force magnitude.`,
          working: [
            'J = Δp = m(v − u)',
            `J = ${mass} × (${final} − ${initial}) = ${display(impulse)} N s`,
            'Faverage = J / Δt',
            `Faverage = ${display(impulse)} / ${duration} = ${display(force)} N`,
          ],
          plot: curve(duration, (t) => force * t),
          xLabel: 'Time (s)',
          yLabel: 'Accumulated impulse (N s)',
        };
      },
      prediction: {
        question: 'The same object stops from the same speed in twice the time. What changes?',
        options: ['Average force magnitude halves', 'Impulse halves', 'Momentum change doubles'],
        answer: 0,
        why: 'Impulse stays fixed by the velocity change. Average force is impulse divided by duration.',
      },
      task: 'Compare stopping from +5 m/s and bouncing to −5 m/s for the same mass and duration. Explain the force difference.',
    },
  ],
};
